import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  collectionGroup,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  increment,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import './Dashboard.css';

const POST_TABS = ['pending', 'approved', 'rejected'];
const FEATURE_REQUEST_TABS = ['inReview', 'inProgress', 'completed', 'rejected'];
const FEATURE_REQUEST_LABELS = {
  inReview: 'In Review',
  inProgress: 'In Progress',
  completed: 'Completed',
  rejected: 'Rejected',
};

// --- Icons (inline SVG, no icon-library dependency) ---
const ICON = {
  posts: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16v12H8l-4 4V4z" />
    </svg>
  ),
  comments: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  ),
  featureRequests: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2 2 7l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
    </svg>
  ),
  reengagement: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  ),
  x: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  ),
  crown: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 8.5 7 11l5-6 5 6 4-2.5L19.5 18h-15L3 8.5z" />
    </svg>
  ),
  logout: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5M21 12H9" />
    </svg>
  ),
};

const NAV_ITEMS = [
  { key: 'posts', label: 'Posts', icon: ICON.posts, statKey: 'pending' },
  { key: 'comments', label: 'Comments', icon: ICON.comments, statKey: 'pendingComments' },
  { key: 'reengagement', label: 'Re-engagement', icon: ICON.reengagement, statKey: null },
  { key: 'featureRequests', label: 'Feature Requests', icon: ICON.featureRequests, statKey: 'pendingFeatureRequests' },
];

const SECTION_SUBTITLE = {
  posts: 'Review and moderate community posts',
  comments: 'All comments require approval before they appear in the app',
  reengagement: 'Segment users by how long they’ve been away and send them back a nudge',
  featureRequests: 'Track and triage what users are asking for',
};

// Pre-filled starting points for re-engagement pushes, mixing a few different
// angles (encouragement, streak loss, community, FOMO, fresh start) so repeat
// sends to the same bucket don't all read the same.
const REENGAGEMENT_MESSAGES = [
  { title: '🌟 We Miss You!', body: "Your streak is waiting. Come back and pick up right where you left off." },
  { title: "🔥 Don't Lose Your Progress", body: "You worked hard to get here. One check-in keeps your momentum alive." },
  { title: '👋 Your Squad Misses You', body: "The community's been active — come see what you've missed." },
  { title: "📈 Others Are Pulling Ahead", body: "People who started with you are hitting new milestones. Jump back in." },
  { title: '🌱 A Fresh Start Awaits', body: "Today's a great day to restart your journey. We're here for you." },
  { title: '💭 Still Thinking About It?', body: "Quitting is hard alone. Open the app and let's tackle it together." },
  { title: '🏆 Unlock Your Next Milestone', body: "You're closer than you think. Come back and keep building your streak." },
  { title: "🤝 We're Rooting For You", body: "Recovery isn't linear. Whenever you're ready, we've got your back." },
];

export default function Dashboard() {
  const { currentUser, signOut } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState('posts'); // 'posts' | 'comments' | 'reengagement' | 'featureRequests'

  // Posts state
  const [activeTab, setActiveTab] = useState('pending');
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);

  // Comments state
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState(null);
  const [selectedComment, setSelectedComment] = useState(null);

  // Feature requests state
  const [featureRequestTab, setFeatureRequestTab] = useState('inReview');
  const [featureRequests, setFeatureRequests] = useState([]);
  const [featureRequestsLoading, setFeatureRequestsLoading] = useState(false);
  const [featureRequestsError, setFeatureRequestsError] = useState(null);
  const [selectedFeatureRequest, setSelectedFeatureRequest] = useState(null);

  // Re-engagement (inactivity buckets + targeted notifications) state
  const [inactivityBuckets, setInactivityBuckets] = useState([]);
  const [bucketsLoading, setBucketsLoading] = useState(false);
  const [bucketsError, setBucketsError] = useState(null);
  const [composeBucket, setComposeBucket] = useState(null); // bucket being composed for, or null
  const [notifTitle, setNotifTitle] = useState('');
  const [notifBody, setNotifBody] = useState('');
  const reengagementShuffleBag = useRef([]); // shuffled indices into REENGAGEMENT_MESSAGES, drained before reshuffling
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [sendError, setSendError] = useState(null);

  const [actionLoading, setActionLoading] = useState(null);
  const [stats, setStats] = useState({ pending: 0, approvedToday: 0, rejectedToday: 0, pendingComments: 0, pendingFeatureRequests: 0 });

  // ── Posts ─────────────────────────────────────────────────────────────────

  const fetchPosts = useCallback(async (status) => {
    setPostsLoading(true);
    try {
      const q = query(collection(db, 'posts'), where('status', '==', status));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setPosts(data);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setPosts([]);
    } finally {
      setPostsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (section === 'posts') fetchPosts(activeTab);
  }, [activeTab, section, fetchPosts]);

  async function handlePostAction(postId, action) {
    setActionLoading(postId);
    try {
      await updateDoc(doc(db, 'posts', postId), {
        status: action,
        reviewedAt: Timestamp.now(),
        reviewedBy: currentUser.uid,
      });
      setPosts((prev) => prev.filter((p) => p.id !== postId));
      if (selectedPost?.id === postId) setSelectedPost(null);
      fetchStats();
    } catch (err) {
      console.error(`Error ${action} post:`, err);
    } finally {
      setActionLoading(null);
    }
  }

  // ── Comments ──────────────────────────────────────────────────────────────

  const fetchPendingComments = useCallback(async () => {
    setCommentsLoading(true);
    setCommentsError(null);
    try {
      const q = query(
        collectionGroup(db, 'comments'),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc'),
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((d) => ({
        id: d.id,
        postId: d.ref.parent.parent.id,
        ...d.data(),
      }));
      setComments(data);
    } catch (err) {
      console.error('Error fetching comments:', err);
      setCommentsError(err.message || 'Failed to load comments');
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (section === 'comments') fetchPendingComments();
  }, [section, fetchPendingComments]);

  async function handleCommentAction(comment, action) {
    const key = `${comment.postId}/${comment.id}`;
    setActionLoading(key);
    try {
      const commentRef = doc(db, 'posts', comment.postId, 'comments', comment.id);
      await updateDoc(commentRef, {
        status: action,
        reviewedAt: Timestamp.now(),
        reviewedBy: currentUser.uid,
      });
      // Keep commentCount in sync: only approved comments count
      if (action === 'approved') {
        await updateDoc(doc(db, 'posts', comment.postId), {
          commentCount: increment(1),
        });
      }
      setComments((prev) => prev.filter((c) => c.id !== comment.id || c.postId !== comment.postId));
      if (selectedComment?.id === comment.id && selectedComment?.postId === comment.postId) {
        setSelectedComment(null);
      }
      fetchStats();
    } catch (err) {
      console.error(`Error ${action} comment:`, err);
    } finally {
      setActionLoading(null);
    }
  }

  // ── Feature requests ─────────────────────────────────────────────────────

  const fetchFeatureRequests = useCallback(async (status) => {
    setFeatureRequestsLoading(true);
    setFeatureRequestsError(null);
    try {
      const q = query(collection(db, 'featureRequests'), where('status', '==', status));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setFeatureRequests(data);
    } catch (err) {
      console.error('Error fetching feature requests:', err);
      setFeatureRequestsError(err.message || 'Failed to load feature requests');
      setFeatureRequests([]);
    } finally {
      setFeatureRequestsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (section === 'featureRequests') fetchFeatureRequests(featureRequestTab);
  }, [featureRequestTab, section, fetchFeatureRequests]);

  async function handleFeatureRequestStatusChange(request, status) {
    setActionLoading(request.id);
    try {
      await updateDoc(doc(db, 'featureRequests', request.id), {
        status,
        updatedAt: Timestamp.now(),
      });
      setFeatureRequests((prev) => prev.filter((r) => r.id !== request.id));
      if (selectedFeatureRequest?.id === request.id) setSelectedFeatureRequest(null);
      fetchStats();
    } catch (err) {
      console.error('Error updating feature request:', err);
    } finally {
      setActionLoading(null);
    }
  }

  async function handlePostDelete(postId) {
    if (!window.confirm('Delete this post permanently? This cannot be undone.')) return;
    setActionLoading(postId);
    try {
      await deleteDoc(doc(db, 'posts', postId));
      setPosts((prev) => prev.filter((p) => p.id !== postId));
      if (selectedPost?.id === postId) setSelectedPost(null);
      fetchStats();
    } catch (err) {
      console.error('Error deleting post:', err);
    } finally {
      setActionLoading(null);
    }
  }

  async function handleFeatureRequestDelete(request) {
    if (!window.confirm('Delete this feature request permanently?')) return;
    setActionLoading(request.id);
    try {
      await deleteDoc(doc(db, 'featureRequests', request.id));
      setFeatureRequests((prev) => prev.filter((r) => r.id !== request.id));
      if (selectedFeatureRequest?.id === request.id) setSelectedFeatureRequest(null);
      fetchStats();
    } catch (err) {
      console.error('Error deleting feature request:', err);
    } finally {
      setActionLoading(null);
    }
  }

  // ── Re-engagement ────────────────────────────────────────────────────────

  const fetchInactivityBuckets = useCallback(async () => {
    setBucketsLoading(true);
    setBucketsError(null);
    try {
      const call = httpsCallable(functions, 'getInactiveUserBuckets');
      const result = await call();
      setInactivityBuckets(result.data?.buckets || []);
    } catch (err) {
      console.error('Error fetching inactivity buckets:', err);
      setBucketsError(err.message || 'Failed to load inactivity buckets');
      setInactivityBuckets([]);
    } finally {
      setBucketsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (section === 'reengagement') fetchInactivityBuckets();
  }, [section, fetchInactivityBuckets]);

  // Draws from a shuffled bag of REENGAGEMENT_MESSAGES so every message is
  // shown once before any repeats, then reshuffles — avoids back-to-back
  // repeats that plain Math.random() picking would allow.
  function nextReengagementMessage() {
    if (reengagementShuffleBag.current.length === 0) {
      const indices = REENGAGEMENT_MESSAGES.map((_, i) => i);
      for (let i = indices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [indices[i], indices[j]] = [indices[j], indices[i]];
      }
      reengagementShuffleBag.current = indices;
    }
    const idx = reengagementShuffleBag.current.pop();
    return REENGAGEMENT_MESSAGES[idx];
  }

  function openCompose(bucket) {
    setComposeBucket(bucket);
    const msg = nextReengagementMessage();
    setNotifTitle(msg.title);
    setNotifBody(msg.body);
    setSendResult(null);
    setSendError(null);
  }

  async function handleSendNotification() {
    if (!composeBucket) return;
    setSending(true);
    setSendError(null);
    setSendResult(null);
    try {
      const call = httpsCallable(functions, 'sendReengagementNotification');
      const result = await call({
        minDays: composeBucket.minDays,
        maxDays: composeBucket.maxDays,
        title: notifTitle.trim(),
        body: notifBody.trim(),
      });
      setSendResult(result.data);
    } catch (err) {
      console.error('Error sending re-engagement notification:', err);
      setSendError(err.message || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  }

  // ── Stats ─────────────────────────────────────────────────────────────────

  const fetchStats = useCallback(async () => {
    try {
      const pendingQ = query(collection(db, 'posts'), where('status', '==', 'pending'));
      const pendingSnap = await getDocs(pendingQ);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayTs = Timestamp.fromDate(today);

      const approvedQ = query(collection(db, 'posts'), where('status', '==', 'approved'));
      const approvedSnap = await getDocs(approvedQ);
      const approvedToday = approvedSnap.docs.filter(
        (d) => d.data().reviewedAt && d.data().reviewedAt.seconds >= todayTs.seconds
      ).length;

      const rejectedQ = query(collection(db, 'posts'), where('status', '==', 'rejected'));
      const rejectedSnap = await getDocs(rejectedQ);
      const rejectedToday = rejectedSnap.docs.filter(
        (d) => d.data().reviewedAt && d.data().reviewedAt.seconds >= todayTs.seconds
      ).length;

      const pendingCommentsQ = query(collectionGroup(db, 'comments'), where('status', '==', 'pending'));
      const pendingCommentsSnap = await getDocs(pendingCommentsQ);

      const pendingFeatureRequestsQ = query(collection(db, 'featureRequests'), where('status', '==', 'inReview'));
      const pendingFeatureRequestsSnap = await getDocs(pendingFeatureRequestsQ);

      setStats({
        pending: pendingSnap.size,
        approvedToday,
        rejectedToday,
        pendingComments: pendingCommentsSnap.size,
        pendingFeatureRequests: pendingFeatureRequestsSnap.size,
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  function formatDate(timestamp) {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }

  async function handleSignOut() {
    await signOut();
    navigate('/sign-in');
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const userInitial = (currentUser?.email || 'A').charAt(0).toUpperCase();

  return (
    <div className="dashboard-shell">
      <aside className="dashboard-sidebar">
        <div className="sidebar-brand">
          <div className="brand-mark">QG</div>
          <div className="brand-text">
            <span className="brand-name">Quit Gooning</span>
            <span className="brand-sub">Admin</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          {NAV_ITEMS.map((item) => {
            const count = stats[item.statKey] || 0;
            return (
              <button
                key={item.key}
                className={`nav-item ${section === item.key ? 'active' : ''}`}
                onClick={() => setSection(item.key)}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
                {count > 0 && <span className="nav-badge">{count}</span>}
              </button>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">{userInitial}</div>
            <span className="user-email" title={currentUser?.email}>{currentUser?.email}</span>
          </div>
          <button onClick={handleSignOut} className="dashboard-signout">
            <span className="nav-icon">{ICON.logout}</span>
            Sign Out
          </button>
        </div>
      </aside>

      <div className="dashboard-content">
        <header className="dashboard-topbar">
          <div>
            <h1 className="topbar-title">{NAV_ITEMS.find((n) => n.key === section)?.label}</h1>
            <p className="topbar-subtitle">{SECTION_SUBTITLE[section]}</p>
          </div>
        </header>

      <main className="dashboard-main">
        {/* Stats */}
        {section !== 'reengagement' && (
        <div className="dashboard-stats">
          <div className="stat-card accent-amber">
            <span className="stat-icon">{ICON.clock}</span>
            <span className="stat-value">{stats.pending}</span>
            <span className="stat-label">Pending Posts</span>
          </div>
          <div className="stat-card accent-violet">
            <span className="stat-icon">{ICON.comments}</span>
            <span className="stat-value">{stats.pendingComments}</span>
            <span className="stat-label">Pending Comments</span>
          </div>
          <div className="stat-card accent-pink">
            <span className="stat-icon">{ICON.featureRequests}</span>
            <span className="stat-value">{stats.pendingFeatureRequests}</span>
            <span className="stat-label">Feature Requests</span>
          </div>
          <div className="stat-card accent-green">
            <span className="stat-icon">{ICON.check}</span>
            <span className="stat-value">{stats.approvedToday}</span>
            <span className="stat-label">Approved Today</span>
          </div>
          <div className="stat-card accent-red">
            <span className="stat-icon">{ICON.x}</span>
            <span className="stat-value">{stats.rejectedToday}</span>
            <span className="stat-label">Rejected Today</span>
          </div>
        </div>
        )}

        {/* ── Posts section ── */}
        {section === 'posts' && (
          <>
            <div className="dashboard-tabs">
              {POST_TABS.map((tab) => (
                <button
                  key={tab}
                  className={`dashboard-tab ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {postsLoading ? (
              <div className="dashboard-loading">Loading posts...</div>
            ) : posts.length === 0 ? (
              <div className="dashboard-empty">No {activeTab} posts.</div>
            ) : (
              <div className="posts-grid">
                {posts.map((post) => (
                  <div key={post.id} className="post-card" onClick={() => setSelectedPost(post)}>
                    <div className="post-card-header">
                      <h3 className="post-title">{post.title || 'Untitled'}</h3>
                      {post.isAuthorPremium && (
                        <span className="pro-badge" title="Posted by a premium user">
                          {ICON.crown} PRO
                        </span>
                      )}
                    </div>
                    <p className="post-content">
                      {(post.content || '').length > 150
                        ? post.content.substring(0, 150) + '...'
                        : post.content}
                    </p>
                    <div className="post-meta">
                      <span>By: {post.userId?.substring(0, 8)}...</span>
                      <span>{formatDate(post.createdAt)}</span>
                    </div>
                    {post.reviewedBy && (
                      <div className="post-review-info">
                        Reviewed {formatDate(post.reviewedAt)}
                        {post.reviewedBy === 'ai-moderator' && ' (AI)'}
                      </div>
                    )}
                    {post.aiModeration && post.status === 'pending' && (
                      <div className="post-review-info" style={{ color: '#f4b740' }}>
                        AI flagged ({post.aiModeration.category}): {post.aiModeration.reason}
                      </div>
                    )}
                    {post.reportCount > 0 && (
                      <div className="post-review-info" style={{ color: '#ef4444' }}>
                        Reported {post.reportCount}x{post.reportedForReview ? ' — pulled for review' : ''}
                      </div>
                    )}
                    <div className="post-actions" onClick={(e) => e.stopPropagation()}>
                      {activeTab === 'pending' && (
                        <>
                          <button
                            className="action-btn approve"
                            onClick={() => handlePostAction(post.id, 'approved')}
                            disabled={actionLoading === post.id}
                          >
                            {actionLoading === post.id ? '...' : 'Approve'}
                          </button>
                          <button
                            className="action-btn reject"
                            onClick={() => handlePostAction(post.id, 'rejected')}
                            disabled={actionLoading === post.id}
                          >
                            {actionLoading === post.id ? '...' : 'Reject'}
                          </button>
                        </>
                      )}
                      <button
                        className="action-btn reject"
                        onClick={() => handlePostDelete(post.id)}
                        disabled={actionLoading === post.id}
                      >
                        {actionLoading === post.id ? '...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Comments section ── */}
        {section === 'comments' && (
          <>
            <div className="comments-section-header">
              <p className="comments-section-subtitle">
                All comments require approval before they appear in the app.
              </p>
              <button className="refresh-btn" onClick={fetchPendingComments}>Refresh</button>
            </div>

            {commentsLoading ? (
              <div className="dashboard-loading">Loading comments...</div>
            ) : commentsError ? (
              <div className="dashboard-empty" style={{color:'#ef4444'}}>Error: {commentsError}</div>
            ) : comments.length === 0 ? (
              <div className="dashboard-empty">No pending comments. All caught up!</div>
            ) : (
              <div className="comments-list">
                {comments.map((comment) => {
                  const key = `${comment.postId}/${comment.id}`;
                  return (
                    <div
                      key={key}
                      className="comment-card"
                      onClick={() => setSelectedComment(comment)}
                    >
                      <div className="comment-post-ref">
                        Post: {comment.postId?.substring(0, 12)}...
                      </div>
                      <p className="comment-text">
                        {(comment.text || '').length > 200
                          ? comment.text.substring(0, 200) + '...'
                          : comment.text}
                      </p>
                      <div className="comment-meta">
                        <span>By: {comment.userId?.substring(0, 8)}...</span>
                        <span>{formatDate(comment.createdAt)}</span>
                      </div>
                      <div className="post-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="action-btn approve"
                          onClick={() => handleCommentAction(comment, 'approved')}
                          disabled={actionLoading === key}
                        >
                          {actionLoading === key ? '...' : 'Approve'}
                        </button>
                        <button
                          className="action-btn reject"
                          onClick={() => handleCommentAction(comment, 'rejected')}
                          disabled={actionLoading === key}
                        >
                          {actionLoading === key ? '...' : 'Reject'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ── Re-engagement section ── */}
        {section === 'reengagement' && (
          <>
            <div className="comments-section-header">
              <p className="comments-section-subtitle">
                Buckets are based on each user's last app open. "Notifiable" is how many of those
                users have push notifications enabled — that's the ceiling on who a send can reach.
              </p>
              <button className="refresh-btn" onClick={fetchInactivityBuckets}>Refresh</button>
            </div>

            {bucketsLoading ? (
              <div className="dashboard-loading">Loading inactivity buckets...</div>
            ) : bucketsError ? (
              <div className="dashboard-empty" style={{color:'#ef4444'}}>Error: {bucketsError}</div>
            ) : inactivityBuckets.length === 0 ? (
              <div className="dashboard-empty">No user data yet.</div>
            ) : (
              <div className="bucket-list">
                {inactivityBuckets.map((bucket) => (
                  <div key={bucket.key} className="bucket-card">
                    <div className="bucket-card-info">
                      <div className="bucket-card-label">{bucket.label}</div>
                      <div className="bucket-card-counts">
                        <span>{bucket.totalCount} users</span>
                        <span className="bucket-card-dot">·</span>
                        <span>{bucket.notifiableCount} notifiable</span>
                      </div>
                    </div>
                    {bucket.key !== 'd0_7' && (
                      <button
                        className="action-btn approve"
                        disabled={bucket.notifiableCount === 0}
                        onClick={() => openCompose(bucket)}
                      >
                        Send Notification
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Feature requests section ── */}
        {section === 'featureRequests' && (
          <>
            <div className="dashboard-tabs">
              {FEATURE_REQUEST_TABS.map((tab) => (
                <button
                  key={tab}
                  className={`dashboard-tab ${featureRequestTab === tab ? 'active' : ''}`}
                  onClick={() => setFeatureRequestTab(tab)}
                >
                  {FEATURE_REQUEST_LABELS[tab]}
                </button>
              ))}
            </div>

            {featureRequestsLoading ? (
              <div className="dashboard-loading">Loading feature requests...</div>
            ) : featureRequestsError ? (
              <div className="dashboard-empty" style={{color:'#ef4444'}}>Error: {featureRequestsError}</div>
            ) : featureRequests.length === 0 ? (
              <div className="dashboard-empty">No {FEATURE_REQUEST_LABELS[featureRequestTab].toLowerCase()} feature requests.</div>
            ) : (
              <div className="posts-grid">
                {featureRequests.map((request) => (
                  <div key={request.id} className="post-card" onClick={() => setSelectedFeatureRequest(request)}>
                    <h3 className="post-title">{request.title || 'Untitled'}</h3>
                    <p className="post-content">
                      {(request.description || '').length > 150
                        ? request.description.substring(0, 150) + '...'
                        : request.description}
                    </p>
                    <div className="post-meta">
                      <span>By: {request.userId?.substring(0, 8)}...</span>
                      <span>{formatDate(request.createdAt)}</span>
                    </div>
                    <div className="post-actions" onClick={(e) => e.stopPropagation()}>
                      <select
                        className="status-select"
                        value={request.status}
                        disabled={actionLoading === request.id}
                        onChange={(e) => handleFeatureRequestStatusChange(request, e.target.value)}
                      >
                        {FEATURE_REQUEST_TABS.map((s) => (
                          <option key={s} value={s}>{FEATURE_REQUEST_LABELS[s]}</option>
                        ))}
                      </select>
                      <button
                        className="action-btn reject"
                        onClick={() => handleFeatureRequestDelete(request)}
                        disabled={actionLoading === request.id}
                      >
                        {actionLoading === request.id ? '...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
      </div>

      {/* Post detail modal */}
      {selectedPost && (
        <div className="modal-overlay" onClick={() => setSelectedPost(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedPost(null)}>&times;</button>
            <div className="modal-title-row">
              <h2 className="modal-title">{selectedPost.title || 'Untitled'}</h2>
              {selectedPost.isAuthorPremium && (
                <span className="pro-badge" title="Posted by a premium user">
                  {ICON.crown} PRO
                </span>
              )}
            </div>
            <div className="modal-meta">
              <span>Author: {selectedPost.userId}</span>
              <span>Created: {formatDate(selectedPost.createdAt)}</span>
              <span>Status: <span className={`status-badge ${selectedPost.status}`}>{selectedPost.status}</span></span>
              {selectedPost.upvoteCount > 0 && <span>Upvotes: {selectedPost.upvoteCount}</span>}
            </div>
            {selectedPost.reviewedBy && (
              <div className="modal-review">
                Reviewed by {selectedPost.reviewedBy} on {formatDate(selectedPost.reviewedAt)}
              </div>
            )}
            {selectedPost.aiModeration && (
              <div className="modal-review" style={{ color: '#f4b740' }}>
                AI moderator — {selectedPost.aiModeration.decision} ({selectedPost.aiModeration.category}): {selectedPost.aiModeration.reason}
              </div>
            )}
            {selectedPost.reportCount > 0 && (
              <div className="modal-review" style={{ color: '#ef4444' }}>
                Reported by {selectedPost.reportCount} user{selectedPost.reportCount === 1 ? '' : 's'}
              </div>
            )}
            <div className="modal-body">{selectedPost.content}</div>
            <div className="modal-actions">
              {selectedPost.status === 'pending' && (
                <>
                  <button
                    className="action-btn approve"
                    onClick={() => handlePostAction(selectedPost.id, 'approved')}
                    disabled={actionLoading === selectedPost.id}
                  >
                    Approve
                  </button>
                  <button
                    className="action-btn reject"
                    onClick={() => handlePostAction(selectedPost.id, 'rejected')}
                    disabled={actionLoading === selectedPost.id}
                  >
                    Reject
                  </button>
                </>
              )}
              <button
                className="action-btn reject"
                onClick={() => handlePostDelete(selectedPost.id)}
                disabled={actionLoading === selectedPost.id}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comment detail modal */}
      {selectedComment && (
        <div className="modal-overlay" onClick={() => setSelectedComment(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedComment(null)}>&times;</button>
            <h2 className="modal-title">Comment Review</h2>
            <div className="modal-meta">
              <span>Post ID: {selectedComment.postId}</span>
              <span>Author: {selectedComment.userId}</span>
              <span>Submitted: {formatDate(selectedComment.createdAt)}</span>
            </div>
            <div className="modal-body">{selectedComment.text}</div>
            <div className="modal-actions">
              <button
                className="action-btn approve"
                onClick={() => handleCommentAction(selectedComment, 'approved')}
                disabled={actionLoading === `${selectedComment.postId}/${selectedComment.id}`}
              >
                Approve
              </button>
              <button
                className="action-btn reject"
                onClick={() => handleCommentAction(selectedComment, 'rejected')}
                disabled={actionLoading === `${selectedComment.postId}/${selectedComment.id}`}
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feature request detail modal */}
      {selectedFeatureRequest && (
        <div className="modal-overlay" onClick={() => setSelectedFeatureRequest(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedFeatureRequest(null)}>&times;</button>
            <h2 className="modal-title">{selectedFeatureRequest.title || 'Untitled'}</h2>
            <div className="modal-meta">
              <span>Submitted by: {selectedFeatureRequest.userId}</span>
              <span>Submitted: {formatDate(selectedFeatureRequest.createdAt)}</span>
              <span>
                Status: <span className={`status-badge ${selectedFeatureRequest.status}`}>
                  {FEATURE_REQUEST_LABELS[selectedFeatureRequest.status] || selectedFeatureRequest.status}
                </span>
              </span>
            </div>
            <div className="modal-body">{selectedFeatureRequest.description}</div>
            <div className="modal-actions">
              <select
                className="status-select"
                value={selectedFeatureRequest.status}
                disabled={actionLoading === selectedFeatureRequest.id}
                onChange={(e) => handleFeatureRequestStatusChange(selectedFeatureRequest, e.target.value)}
              >
                {FEATURE_REQUEST_TABS.map((s) => (
                  <option key={s} value={s}>{FEATURE_REQUEST_LABELS[s]}</option>
                ))}
              </select>
              <button
                className="action-btn reject"
                onClick={() => handleFeatureRequestDelete(selectedFeatureRequest)}
                disabled={actionLoading === selectedFeatureRequest.id}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Re-engagement notification compose modal */}
      {composeBucket && (
        <div className="modal-overlay" onClick={() => !sending && setComposeBucket(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setComposeBucket(null)} disabled={sending}>&times;</button>
            <h2 className="modal-title">Notify: {composeBucket.label}</h2>
            <div className="modal-meta">
              <span>{composeBucket.totalCount} users</span>
              <span>{composeBucket.notifiableCount} reachable (notifications enabled)</span>
            </div>

            {sendResult ? (
              <>
                <div className="modal-body">
                  Sent to {sendResult.tokensSent} of {sendResult.matchedUsers} matched users.
                  {sendResult.failureCount > 0 && ` ${sendResult.failureCount} deliveries failed.`}
                  {sendResult.purged > 0 && ` ${sendResult.purged} dead tokens were cleaned up.`}
                </div>
                <div className="modal-actions">
                  <button className="action-btn approve" onClick={() => setComposeBucket(null)}>Done</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, margin: '16px 0' }}>
                  <input
                    type="text"
                    placeholder="Notification title"
                    value={notifTitle}
                    onChange={(e) => setNotifTitle(e.target.value)}
                    maxLength={100}
                    disabled={sending}
                    className="notif-input"
                  />
                  <textarea
                    placeholder="Notification body"
                    value={notifBody}
                    onChange={(e) => setNotifBody(e.target.value)}
                    maxLength={200}
                    rows={4}
                    disabled={sending}
                    className="notif-input"
                    style={{ resize: 'vertical' }}
                  />
                  <button
                    type="button"
                    className="refresh-btn"
                    style={{ alignSelf: 'flex-start' }}
                    onClick={() => {
                      const msg = nextReengagementMessage();
                      setNotifTitle(msg.title);
                      setNotifBody(msg.body);
                    }}
                    disabled={sending}
                  >
                    🔀 Shuffle message
                  </button>
                </div>
                {sendError && (
                  <div className="dashboard-empty" style={{ color: '#ef4444', padding: '0 0 12px' }}>
                    Error: {sendError}
                  </div>
                )}
                <div className="modal-actions">
                  <button
                    className="action-btn approve"
                    onClick={handleSendNotification}
                    disabled={sending || !notifTitle.trim() || !notifBody.trim()}
                  >
                    {sending ? 'Sending...' : `Send to ${composeBucket.notifiableCount}`}
                  </button>
                  <button className="action-btn reject" onClick={() => setComposeBucket(null)} disabled={sending}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
